import type { Metrics } from 'shared/state/types';
import { useAbility, PERM } from 'shared/access';
import { ninesToPercent, ninesLabel, formatCapacity, formatKw, formatPerTb, formatFixed } from 'shared/lib/format';
import type { RailCtx, RailTile } from 'shared/state/railTypes';

// Tile TYPES live in shared/state/railTypes so features/live-rail can consume
// them without importing app/ (features → app is a forbidden boundary — see
// task-5-brief's "Boundary note"). Re-exported here so nothing importing
// from this module needs to change.
export type { RailCtx, RailTile, RailGroup } from 'shared/state/railTypes';

const n = (m: Metrics | null, key: string): number => Number(m?.[key] ?? 0);
// CONF-227: a nines figure is "—" when absent, never a fake percentage.
const ninesPair = (m: Metrics | null, durabilityKey: string, availabilityKey: string) => ({
  percent: `${ninesToPercent(n(m, durabilityKey)) ?? '—'} / ${ninesToPercent(n(m, availabilityKey)) ?? '—'}`,
  nines: `${ninesLabel(n(m, durabilityKey)) ?? '—'} / ${ninesLabel(n(m, availabilityKey)) ?? '—'} nines`,
});
const flashOn = ({ config }: RailCtx) => Boolean(config.afaEnabled);
const mixedOn = ({ config }: RailCtx) => Boolean(config.hybridEnabled);
// Classic prints percentages with up to one decimal and no forced trailing zero
// ("5.6", "6"); formatCapacity(v, 1) would force "6.0".
const pct = (v: number) => (isFinite(v) ? v : 0).toLocaleString(undefined, { maximumFractionDigits: 1 });

/* IOPS (afaIops / hybridIops, CONF-125) and metadata rate are deliberately ABSENT:
 * hidden by product decision (spec §4.5). Adding a readout is one entry here. */
export const RAIL_TILES: RailTile[] = [
  // ---- System Attributes -------------------------------------------------
  {
    id: 'rawCapacity', group: 'system', label: 'RAW Capacity', requires: [], keys: ['totalRawCapacity'],
    value: (c) => formatCapacity(n(c.metrics, 'totalRawCapacity')),
    unit: (c) => c.unit,
    // Classic parity (SystemAttributesCard rawCapacityNode): NVMe share of raw, then the NVMe total.
    hint: (c) => `${pct(n(c.metrics, 'ratioTotalSsdHdd'))}%, ${formatCapacity(n(c.metrics, 'totalSsdCapacity'))} ${c.unit} NVMe`,
  },
  {
    id: 'useableCapacity', group: 'system', label: 'Useable Capacity', requires: [], keys: ['totalEffectiveCapacity'],
    value: (c) => formatCapacity(n(c.metrics, 'totalEffectiveCapacity')),
    unit: (c) => c.unit,
    hint: (c) => `${formatCapacity(n(c.metrics, 'useableEff'))}% util`,
  },
  {
    id: 'effectiveCapacity', group: 'system', label: 'Effective Capacity', requires: [], keys: ['totalCompressedEffectiveCapacity'],
    value: (c) => formatCapacity(n(c.metrics, 'totalCompressedEffectiveCapacity')),
    unit: (c) => c.unit,
    // CONF-219 parity with classic's effectiveCapacityNode
    // (SystemAttributesCard.jsx): the NVMe share of the effective figure —
    // the flash set's effective capacity plus the mixed fleet's NVMe burst
    // tier — printed only while the capacity set is on, as classic does.
    hint: (c) => (mixedOn(c)
      ? `${formatCapacity((flashOn(c) ? n(c.metrics, 'afaCompressedEffectiveCapacity') : 0) + n(c.metrics, 'hybridBurstCapacity'))} ${c.unit} NVMe`
      : undefined),
  },
  { id: 'inodes', group: 'system', label: 'iNodes', requires: [], value: (c) => formatCapacity(n(c.metrics, 'totalInodes')), unit: () => 'M' },

  // ---- Flash Metrics -----------------------------------------------------
  {
    id: 'flashRaw', group: 'flash', label: 'Flash RAW', requires: [], keys: ['totalAfaRawCapacity'], when: flashOn, tone: 'flash',
    value: (c) => formatCapacity(n(c.metrics, 'totalAfaRawCapacity')), unit: (c) => c.unit,
  },
  {
    id: 'flashUseable', group: 'flash', label: 'Flash Useable', requires: [], keys: ['afaEffectiveCapacity'], when: flashOn, tone: 'flash',
    value: (c) => formatCapacity(n(c.metrics, 'afaEffectiveCapacity')), unit: (c) => c.unit,
  },
  {
    // Classic parity (afaEffectiveCapacityNode): the flash set's compressed effective capacity.
    id: 'flashEffective', group: 'flash', label: 'Flash Effective', requires: [], keys: ['afaCompressedEffectiveCapacity'], when: flashOn, tone: 'flash',
    value: (c) => formatCapacity(n(c.metrics, 'afaCompressedEffectiveCapacity')), unit: (c) => c.unit,
  },
  {
    id: 'flashThroughput', group: 'flash', label: 'Flash Throughput (R/W)', requires: [], keys: ['afaReadTransferRate', 'afaWriteTransferRate'], when: flashOn, tone: 'flash',
    value: (c) => `${formatFixed(n(c.metrics, 'afaReadTransferRate'))} / ${formatFixed(n(c.metrics, 'afaWriteTransferRate'))}`,
    unit: (c) => c.throughputUnit,
  },
  {
    id: 'flashNines', group: 'flash', label: 'Flash Durability / Availability', requires: [], when: flashOn, tone: 'flash',
    value: (c) => ninesPair(c.metrics, 'afaDurabilityNines', 'afaAvailabilityNines').percent,
    unit: () => '%',
    hint: (c) => ninesPair(c.metrics, 'afaDurabilityNines', 'afaAvailabilityNines').nines,
  },

  // ---- Mixed Fleet Metrics ----------------------------------------------
  {
    id: 'mixedRaw', group: 'mixed', label: 'Mixed Fleet RAW', requires: [], keys: ['totalHybridRawCapacity'], when: mixedOn, tone: 'brand',
    value: (c) => formatCapacity(n(c.metrics, 'totalHybridRawCapacity')), unit: (c) => c.unit,
  },
  {
    id: 'mixedUseable', group: 'mixed', label: 'Mixed Fleet Useable', requires: [], keys: ['hybridEffectiveCapacity'], when: mixedOn, tone: 'brand',
    value: (c) => formatCapacity(n(c.metrics, 'hybridEffectiveCapacity')), unit: (c) => c.unit,
  },
  {
    // Classic parity (hybridEffectiveCapacityNode): the capacity set's compressed effective capacity.
    id: 'mixedEffective', group: 'mixed', label: 'Mixed Fleet Effective', requires: [], keys: ['hybridCompressedEffectiveCapacity'], when: mixedOn, tone: 'brand',
    value: (c) => formatCapacity(n(c.metrics, 'hybridCompressedEffectiveCapacity')), unit: (c) => c.unit,
  },
  {
    // CONF-219 parity with classic's hybridNvmeThroughputNode: the mixed
    // fleet's NVMe (burst) rate beside its sustained HDD rate, with the burst
    // capacity that rate applies to as the hint. CONF-263: the hint no longer
    // says "burst"; it is worded like the Effective Capacity tile's NVMe hint.
    id: 'mixedNvmeThroughput', group: 'mixed', label: 'NVMe Throughput (R/W)', requires: [], keys: ['hybridBurstReadTransferRate', 'hybridBurstWriteTransferRate'], when: mixedOn, tone: 'brand',
    value: (c) => `${formatFixed(n(c.metrics, 'hybridBurstReadTransferRate'))} / ${formatFixed(n(c.metrics, 'hybridBurstWriteTransferRate'))}`,
    unit: (c) => c.throughputUnit,
    hint: (c) => `${formatFixed(n(c.metrics, 'hybridBurstCapacity'))} ${c.unit} NVMe`,
  },
  {
    id: 'mixedThroughput', group: 'mixed', label: 'HDD Throughput (R/W)', requires: [], keys: ['hybridReadTransferRate', 'hybridWriteTransferRate'], when: mixedOn, tone: 'brand',
    value: (c) => `${formatFixed(n(c.metrics, 'hybridReadTransferRate'))} / ${formatFixed(n(c.metrics, 'hybridWriteTransferRate'))}`,
    unit: (c) => c.throughputUnit,
  },
  {
    id: 'mixedNines', group: 'mixed', label: 'Mixed Fleet Durability / Availability', requires: [], when: mixedOn, tone: 'brand',
    value: (c) => ninesPair(c.metrics, 'hybridDurabilityNines', 'hybridAvailabilityNines').percent,
    unit: () => '%',
    hint: (c) => ninesPair(c.metrics, 'hybridDurabilityNines', 'hybridAvailabilityNines').nines,
  },

  // ---- Physical ----------------------------------------------------------
  {
    id: 'power', group: 'physical', label: 'Power (typical / max)', requires: [],
    value: (c) => `${formatKw(n(c.metrics, 'totalNominalPower'))} / ${formatKw(n(c.metrics, 'totalMaxPower'))}`,
    unit: () => 'kW',
  },
  {
    // Both units live inline in `value` (nothing in `unit`) because this tile
    // composes two figures with different units — kg for weight, RU for rack
    // units — unlike every other tile here, which has one unit for its whole
    // value. This matches the classic card byte-for-byte
    // (SystemAttributesCard.jsx:102-108: "{weight} kg / {ru} RU").
    // Note: systemMetrics.ts:210 comments totalWeight as "lbs", but the
    // productDb values (17.6-18.6 for a 2U node, 177.1 for a loaded 108-bay
    // JBOD) are only plausible as kg — a pre-existing discrepancy in the
    // classic shell, tracked separately; not this phase's job to resolve.
    id: 'weightRu', group: 'physical', label: 'Weight / Rack Units', requires: [],
    value: (c) => `${formatFixed(n(c.metrics, 'totalWeight'))} kg / ${formatFixed(n(c.metrics, 'totalRU'))} RU`,
  },

  // ---- Price-adjacent efficiency (needs pricing rights) -------------------
  // `keys` name the capacity denominator: an exec capacity override re-derives
  // $/TB (useDisplayMetrics), so the tile pairs exactly when its total moved.
  {
    id: 'pricePerRaw', group: 'system', label: '$ / RAW TB', requires: [PERM.PRICING_VIEW], keys: ['totalRawCapacity'],
    value: (c) => formatPerTb(n(c.metrics, 'dollarsPerRawTB')),
  },
  {
    id: 'pricePerEffective', group: 'system', label: '$ / Effective TB', requires: [PERM.PRICING_VIEW], keys: ['totalCompressedEffectiveCapacity'],
    value: (c) => formatPerTb(n(c.metrics, 'dollarsPerCompressedEffectiveTB')),
  },
];

export function useVisibleRailTiles(ctx: RailCtx): RailTile[] {
  const ability = useAbility();
  return RAIL_TILES.filter((t) => t.requires.every((p) => ability.can(p)) && (t.when?.(ctx) ?? true));
}
